import React, { useState, useMemo } from 'react';
import { PLATFORMS, PLATFORM_ORDER, findCrossoverVolume } from '../data/platforms';
import NumberStepper from './NumberStepper';

const fmt = (v, sym = '$') => {
  if (!Number.isFinite(v)) return '—';
  const abs = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (abs >= 10000) return `${sign}${sym}${abs.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
  return `${sign}${sym}${abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

export default function PlatformsPanel({ inputs, settings, currencySymbol = '$' }) {
  const [monthlyVolume, setMonthlyVolume] = useState('30');

  const hasProduct = inputs?.price && parseFloat(inputs.price) > 0;

  // Compute results across all three platforms
  const results = useMemo(() => {
    if (!hasProduct) return null;
    const params = {
      price: parseFloat(inputs.price) || 0,
      shipping: parseFloat(inputs.shipping) || 0,
      cost: (parseFloat(inputs.cost) || 0)
        + (parseFloat(inputs.packaging) || 0)
        + (parseFloat(inputs.laborHours) || 0) * (parseFloat(inputs.laborRate) || 0),
      monthlyVolume: parseFloat(monthlyVolume) || 0,
      offsiteRate: settings?.offsiteEnabled ? (settings.offsiteRate || 0.15) : 0,
    };

    return PLATFORM_ORDER.map(key => ({
      key,
      meta: PLATFORMS[key],
      data: PLATFORMS[key].calculate(params),
    }));
  }, [inputs, settings, monthlyVolume, hasProduct]);

  // Find which platform wins at the user's current volume + the crossover points
  const winnerKey = useMemo(() => {
    if (!results) return null;
    return results.slice().sort((a, b) => b.data.monthlyProfit - a.data.monthlyProfit)[0].key;
  }, [results]);

  const crossovers = useMemo(() => {
    if (!hasProduct) return [];
    return findCrossoverVolume(
      parseFloat(inputs.price) || 0,
      parseFloat(inputs.shipping) || 0,
      (parseFloat(inputs.cost) || 0)
        + (parseFloat(inputs.packaging) || 0)
        + (parseFloat(inputs.laborHours) || 0) * (parseFloat(inputs.laborRate) || 0),
      { offsiteRate: settings?.offsiteEnabled ? (settings.offsiteRate || 0.15) : 0 },
    );
  }, [inputs, settings, hasProduct]);

  if (!hasProduct) {
    return (
      <div className="sketch-card">
        <h2 className="font-display text-2xl text-ink mb-1">🏢 Platforms</h2>
        <p className="font-body text-sm text-ink/78 mb-4">
          Calculate a product first, then see how Etsy stacks up against Shopify and Amazon Handmade.
        </p>
      </div>
    );
  }

  return (
    <div className="sketch-card">
      <div className="flex items-start justify-between gap-3 mb-1 flex-wrap">
        <h2 className="font-display text-2xl text-ink">🏢 Platform Comparison</h2>
        <span className="font-body text-xs text-ink/72" style={{ marginTop: '0.5rem' }}>
          Etsy · Shopify · Amazon Handmade
        </span>
      </div>
      <p className="font-body text-sm text-ink/78 mb-4">
        At your current pricing, where would this product earn the most each month?
      </p>

      {/* Monthly volume input */}
      <div className="mb-5">
        <label className="font-body text-sm text-ink/80 block mb-2" style={{ fontWeight: 600 }}>
          📦 Sales per month
        </label>
        <div style={{ maxWidth: '180px' }}>
          <NumberStepper
            value={monthlyVolume}
            onChange={setMonthlyVolume}
            step={5}
            min={0}
            placeholder="30"
            inputClassName="sketch-input"
          />
        </div>
      </div>

      {/* Three platform cards side-by-side */}
      <div
        className="platform-grid mb-5"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
          gap: '0.75rem',
        }}
      >
        {results.map(({ key, meta, data }) => {
          const isWinner = key === winnerKey;
          const profitColor = data.monthlyProfit >= 0 ? '#047857' : '#9f1239';
          return (
            <div
              key={key}
              style={{
                background: isWinner ? meta.accent : '#fefdf8',
                border: isWinner ? `2.5px solid ${meta.color}` : '1.5px solid rgba(26,26,46,0.12)',
                borderRadius: '0.75rem',
                padding: '0.85rem',
                position: 'relative',
                boxShadow: isWinner ? `0 1px 2px rgba(0,0,0,0.04), 0 4px 12px ${meta.accent}` : '0 1px 2px rgba(26,26,46,0.04)',
                minWidth: 0,
              }}
            >
              {isWinner && (
                <div
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    left: '0.85rem',
                    background: meta.color,
                    color: '#fefdf8',
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.74rem',
                    fontWeight: 700,
                    padding: '0.15rem 0.5rem',
                    borderRadius: '0.3rem',
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                  }}
                >
                  ★ Best at {monthlyVolume}/mo
                </div>
              )}

              {/* Platform header */}
              <div className="flex items-center gap-1.5 mb-1">
                <span style={{ fontSize: '1.2rem' }} aria-hidden="true">{meta.icon}</span>
                <h3
                  className="font-display text-sm"
                  style={{
                    color: meta.color,
                    fontWeight: 700,
                    letterSpacing: '0.01em',
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {meta.name}
                </h3>
              </div>

              {/* Headline: monthly profit */}
              <div className="mb-3">
                <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>
                  Net profit / month
                </p>
                <p
                  className="font-numbers"
                  style={{
                    fontSize: '1.4rem',
                    fontWeight: 700,
                    color: profitColor,
                    fontVariantNumeric: 'tabular-nums',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.1,
                  }}
                >
                  {fmt(data.monthlyProfit, currencySymbol)}
                </p>
                <p className="font-body text-xs text-ink/72 mt-0.5" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {fmt(data.perSaleProfit, currencySymbol)} / sale
                </p>
              </div>

              {/* Effective fee rate */}
              <div
                style={{
                  padding: '0.4rem 0.6rem',
                  background: '#ffffff',
                  border: '1px solid rgba(26,26,46,0.1)',
                  borderRadius: '0.4rem',
                  marginBottom: '0.75rem',
                }}
              >
                <p className="font-body text-xs text-ink/72" style={{ fontWeight: 600 }}>
                  Platform takes
                </p>
                <p
                  className="font-numbers"
                  style={{
                    fontSize: '1rem',
                    fontWeight: 700,
                    color: meta.color,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {data.effectiveFeeRatePct.toFixed(1)}%
                </p>
              </div>

              {/* Fee breakdown */}
              <div style={{ paddingTop: '0.5rem', borderTop: '1px dashed rgba(26,26,46,0.18)' }}>
                <p className="font-body text-xs text-ink/72 mb-1" style={{ fontWeight: 600 }}>
                  Where it goes:
                </p>
                {data.breakdownLines.map((line, i) => (
                  <div key={i} className="flex justify-between items-baseline" style={{ marginBottom: '0.15rem' }}>
                    <span
                      className="font-body"
                      style={{
                        fontSize: '0.86rem',
                        color: 'rgba(26,26,46,0.7)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        marginRight: '0.4rem',
                      }}
                    >
                      {line.label}
                    </span>
                    <span
                      className="font-numbers"
                      style={{
                        fontSize: '0.88rem',
                        fontWeight: 600,
                        color: line.isFixed ? '#7e22ce' : '#1a1a2e',
                        fontVariantNumeric: 'tabular-nums',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {line.note ? <em style={{ color: '#047857', fontSize: '0.74rem', fontWeight: 500 }}>{line.note}</em>
                                  : fmt(line.value, currencySymbol)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Pros/cons */}
              <div style={{ paddingTop: '0.5rem', marginTop: '0.5rem', borderTop: '1px dashed rgba(26,26,46,0.18)' }}>
                <ul className="font-body" style={{ fontSize: '0.86rem', color: 'rgba(26,26,46,0.65)', listStyle: 'none', padding: 0, margin: 0, lineHeight: 1.45 }}>
                  {meta.pros.slice(0, 2).map((p, i) => (
                    <li key={`p${i}`}>✓ {p}</li>
                  ))}
                  {meta.cons.slice(0, 1).map((c, i) => (
                    <li key={`c${i}`} style={{ color: 'rgba(159, 18, 57, 0.7)' }}>✗ {c}</li>
                  ))}
                </ul>
              </div>
            </div>
          );
        })}
      </div>

      {/* Crossover insight — when does the ranking flip? */}
      {crossovers.length > 0 && (
        <div
          style={{
            padding: '0.75rem 0.95rem',
            background: 'rgba(180, 83, 9, 0.05)',
            border: '1px dashed rgba(180, 83, 9, 0.35)',
            borderRadius: '0.5rem',
            marginBottom: '0.75rem',
          }}
        >
          <p className="font-body text-sm text-ink mb-1" style={{ fontWeight: 600 }}>
            💡 Volume crossover
          </p>
          <p className="font-body text-xs text-ink/82" style={{ lineHeight: 1.55 }}>
            {crossovers.map((t, i) => (
              <span key={i}>
                At <strong>{t.atVolume}+ sales/month</strong>,{' '}
                <strong style={{ color: PLATFORMS[t.to].color }}>{PLATFORMS[t.to].name}</strong>{' '}
                overtakes <strong style={{ color: PLATFORMS[t.from].color }}>{PLATFORMS[t.from].name}</strong>.
                {i < crossovers.length - 1 && ' '}
              </span>
            ))}
            {' '}
            <em>Use the volume input above to test different scales.</em>
          </p>
        </div>
      )}

      <p className="font-note text-xs text-ink/78 mt-4">
        ~ rates verified Apr 2026 · simplified comparison · does not include ads, app costs, or platform-specific perks ~
      </p>
    </div>
  );
}
